// A just-completed checkout, waiting for its entitlement rows to catch up.
//
// The rows My strategies renders are written at the end of a chain (checkout webhook -> Stripe
// subscription -> Stripe invoice -> invoice.paid webhook), so for a few seconds after payment the
// list is legitimately short. This records what a complete list looks like, so the page can wait
// for the rows rather than tell someone who has just paid that they own nothing.
//
// sessionStorage rather than the URL: the customer never sees the mechanism, there's nothing to
// tamper with, and it survives the refresh a worried customer is most likely to reach for. Same
// storage the cart uses, so it's tab-scoped and dies with the tab.

const STORAGE_KEY = "scryer-fo-pending-checkout-v1";

// Belt and braces against a marker that was never resolved — a tab left open overnight should not
// come back to a wait. Far longer than the wait itself, which the page caps separately.
const MAX_AGE_MS = 30 * 60 * 1000;

export interface PendingCheckout {
  /** How many subscriptions the list holds once the purchase has landed. */
  expectedCount: number;
  /** When payment completed, so the wait's cap survives a refresh. */
  startedAt: number;
  /** The card session paid with, so the wait can ask whether the card was declined. */
  sessionId?: string;
}

export function beginPendingCheckout(expectedCount: number, sessionId: string): void {
  if (expectedCount <= 0) return;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ expectedCount, startedAt: Date.now(), sessionId })
    );
  } catch {
    // Storage disabled or full. The wait is a nicety, not the purchase — carry on without it.
  }
}

export function readPendingCheckout(): PendingCheckout | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as PendingCheckout).expectedCount !== "number" ||
      typeof (parsed as PendingCheckout).startedAt !== "number"
    ) {
      clearPendingCheckout();
      return null;
    }

    const pending = parsed as PendingCheckout;
    if (Date.now() - pending.startedAt > MAX_AGE_MS) {
      clearPendingCheckout();
      return null;
    }

    return pending;
  } catch {
    return null;
  }
}

export function clearPendingCheckout(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — see beginPendingCheckout.
  }
}
